import { useMemo, useState } from 'react'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { GridRenderCellParams } from '@mui/x-data-grid-premium'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import {
  SheduleTableProps,
  TrialRowType,
  WorkstationDownTimeRowType,
} from './Scheduler.types'
import {
  DownTimeMiniTimeline,
  DownTimeWindowsDialog,
} from './DownTimeWindowsEditor'
import { parsePeriods } from './RulesWorkbench.timeline'
import { FONT_SANS, TOKENS } from './SchedulerDesign'

/**
 * Trials (TR-1). Experimental production in a hard-fixed window on a line, a
 * kitchen, or both. Either may be left blank — a line-only or a kitchen-only
 * trial — but not both, since a trial that uses neither books nothing.
 *
 * Presented on the downtime tab because that is where the client asked to type
 * it, but written to its own Delta table — see `TrialRowType` for why the two
 * cannot share one.
 *
 * Everything this table validates, the engine also rejects at preprocess with a
 * named warning. Flagging it here means the planner sees it while typing rather
 * than in a run log afterwards; the engine stays the authority.
 */

/**
 * Read a boolean that may have come back as a string.
 *
 * The service writes every column with pandas' `dtype=str`, so a `false`
 * posted from here is stored and returned as the STRING `'False'` — and a
 * non-empty string is truthy in JavaScript. That is why unticking Active
 * appeared not to save: the value was written correctly and the checkbox
 * sprang back to ticked when it was read again. (The engine was never
 * fooled — Rust's `get_bool` lowercases and maps "false" — so an inactive
 * trial really was left out of the run.)
 *
 * Mirrors the service's own `_is_truthy`, which exists for the same reason.
 */
const asBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes'
  }
  return false
}

/**
 * The clean flags, which are **absent-means-ON** — the opposite of `asBool`
 * above, and deliberately so.
 *
 * `active` is absent-means-active by convention; these are absent-means-ON
 * because every trial written before the columns existed was cleaned on both
 * sides, and reading a missing column as `false` would silently strip the
 * cleans off every historical row. Only an explicit false switches one off.
 */
const asCleanFlag = (v: unknown): boolean => {
  if (v === undefined || v === null || v === '') return true
  return asBool(v)
}

const dataConfig = {
  required: ['trialId'],
  optional: [
    'line',
    'lineWindow',
    'trialName',
    'requiresKitchen',
    'kitchen',
    'kitchenWindow',
    'productCode',
    'active',
    'lineCleanBefore',
    'lineCleanAfter',
    'kitchenCleanBefore',
    'kitchenCleanAfter',
  ],
}

/** A trial is a single window; the engine rejects a multi-window one. */
const MAX_TRIAL_WINDOWS = 1

const isBlank = (v: unknown) => v == null || String(v).trim() === ''

/** Does the trial use a line? A blank line is a kitchen-only trial. */
const usesLine = (row: TrialRowType) => !isBlank(row.line)

/**
 * Does the trial use a kitchen? Either the box is ticked, or — for a trial
 * with no line — a kitchen is named, since the kitchen is then the whole
 * trial and the engine takes it as needed whatever the box says.
 */
const usesKitchen = (row: TrialRowType) =>
  asBool(row.requiresKitchen) || (!usesLine(row) && !isBlank(row.kitchen))

/** A trial must use at least one unit; the engine rejects one using neither. */
const usesNeither = (row: TrialRowType) =>
  isBlank(row.line) && isBlank(row.kitchen)

/** The one window a trial value holds, or null if it holds none or several. */
const singleWindow = (value: unknown): [number, number] | null => {
  const windows = parsePeriods(value as string | null | undefined)
  if (windows.length !== 1) return null
  const [start, end] = windows[0]
  return end > start && start >= 0 ? [start, end] : null
}

const overlaps = (a: [number, number], b: [number, number]) =>
  !(a[1] <= b[0] || a[0] >= b[1])

/** The hours two overlapping windows share, as "a–b". */
const sharedHours = (a: [number, number], b: [number, number]) =>
  `${Math.max(a[0], b[0])}–${Math.min(a[1], b[1])}`

/** Red inline note under a cell — used where a cell is not inline-editable, so
 *  `preProcessEditCellProps`'s error state never gets a chance to show. */
const CellWarning = ({ text }: { text: string }) => (
  <Box
    sx={{
      fontSize: 10.5,
      fontFamily: FONT_SANS,
      color: TOKENS.stateFailed,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
    title={text}
  >
    {text}
  </Box>
)

type TrialsTableProps = SheduleTableProps<TrialRowType> & {
  onAddRow: () => void
  /** Lines a trial may run on, from the week's packing routes. */
  lineOptions: string[]
  /** Kitchens a trial may use, same source. */
  kitchenOptions: string[]
  /** The week's downtime rows, checked against a trial's windows on save. */
  downtimeRows?: WorkstationDownTimeRowType[]
}

/**
 * The warnings a save must confirm before it goes through, one line per
 * problem, for every changed row that is active.
 *
 * Three checks, all against the rows as they will be once the save lands:
 * a window over hours its line or kitchen is already down, and a window over
 * another active trial on the same line or kitchen. Each names the hours that
 * actually overlap, not just the other window. They are warnings, not blocks:
 * the planner may mean it, and the engine runs both. A trial runs through
 * downtime as entered; two trials on one unit at once run together as one
 * occupancy, cleaned once before the first and once after the last (TR-1).
 */
const trialSaveWarnings = (
  changed: Record<number, TrialRowType>,
  rows: TrialRowType[],
  downtimeRows: WorkstationDownTimeRowType[]
): { warnings: string[]; trialClash: boolean } => {
  const merged = rows.map((r) => changed[r.id] ?? r)
  for (const row of Object.values(changed)) {
    if (!merged.some((r) => r.id === row.id)) merged.push(row)
  }
  const downtimeFor = (resource: string): [number, number][] =>
    downtimeRows
      .filter((d) => String(d.resource ?? '').trim() === resource)
      .flatMap((d) => parsePeriods(d.downTime))

  const warnings: string[] = []
  // Both rows of a clashing pair may be in the save; report the pair once.
  const reported = new Set<string>()
  let trialClash = false
  const units = [
    ['lineWindow', 'line', 'line'],
    ['kitchenWindow', 'kitchen', 'kitchen'],
  ] as const
  for (const row of Object.values(changed)) {
    if (!asBool(row.active)) continue
    const name = row.trialId || 'This trial'
    if (usesNeither(row)) {
      warnings.push(
        `${name} names neither a line nor a kitchen, so it will not run. A trial needs at least one of them.`
      )
      continue
    }
    for (const [field, key, unit] of units) {
      if (field === 'lineWindow' && !usesLine(row)) continue
      if (field === 'kitchenWindow' && !usesKitchen(row)) continue
      const resource = String(row[key] ?? '').trim()
      const window = singleWindow(row[field])
      if (!resource || !window) continue

      for (const d of downtimeFor(resource).filter((d) =>
        overlaps(window, d)
      )) {
        warnings.push(
          `${name} runs through downtime on ${unit} ${resource} for hours ${sharedHours(window, d)} (${name} ${window[0]}–${window[1]}, downtime ${d[0]}–${d[1]}).`
        )
      }

      for (const other of merged) {
        if (other.id === row.id || !asBool(other.active)) continue
        if (field === 'lineWindow' && !usesLine(other)) continue
        if (field === 'kitchenWindow' && !usesKitchen(other)) continue
        if (String(other[key] ?? '').trim() !== resource) continue
        const otherWindow = singleWindow(other[field])
        if (!otherWindow || !overlaps(window, otherWindow)) continue
        const pair = [row.id, other.id].sort((a, b) => a - b).join('|')
        if (reported.has(`${field}|${pair}`)) continue
        reported.add(`${field}|${pair}`)
        trialClash = true
        const otherName = other.trialId || 'another trial'
        warnings.push(
          `${name} and ${otherName} both use ${unit} ${resource} for hours ${sharedHours(window, otherWindow)} (${name} ${window[0]}–${window[1]}, ${otherName} ${otherWindow[0]}–${otherWindow[1]}).`
        )
      }
    }
  }
  return { warnings, trialClash }
}

export const SchedulerTrialsTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
  onAddRow,
  lineOptions,
  kitchenOptions,
  downtimeRows = [],
}: TrialsTableProps) => {
  // Which (row, field) the window dialog is editing. Both windows use the same
  // editor, so the field has to travel with the row.
  const [editing, setEditing] = useState<{
    row: TrialRowType
    field: 'lineWindow' | 'kitchenWindow'
  } | null>(null)

  const rows = data ?? []

  // A save with warnings waits here until the planner answers the popup.
  const [pending, setPending] = useState<{
    changed: Record<number, TrialRowType>
    warnings: string[]
    trialClash: boolean
  } | null>(null)
  // Bumped on "No" to remount the grid. The toolbar's Save has already moved
  // the edit into the grid's rows and cleared its unsaved state by the time it
  // calls us, so without a remount the refused edit would stay on screen
  // looking saved.
  const [gridKey, setGridKey] = useState(0)

  // Every save from this table goes through here — the toolbar's Save and the
  // window popup alike — so neither can skip the checks.
  const saveWithChecks = (changed: Record<number, TrialRowType>) => {
    const { warnings, trialClash } = trialSaveWarnings(
      changed,
      rows,
      downtimeRows
    )
    if (warnings.length === 0) {
      updateTableData(changed)
      return
    }
    setPending({ changed, warnings, trialClash })
  }

  // TR-1 — two trials on one resource at the same hours. Allowed (the save
  // popup asks the planner to confirm), but worth seeing while typing, with
  // the hours they share.
  const clashWith = (
    row: TrialRowType,
    field: 'lineWindow' | 'kitchenWindow'
  ): string | null => {
    const window = singleWindow(row[field])
    if (!window) return null
    const resource = field === 'lineWindow' ? row.line : row.kitchen
    if (isBlank(resource)) return null
    for (const other of rows) {
      if (other.id === row.id) continue
      if (!asBool(other.active)) continue
      const otherResource = field === 'lineWindow' ? other.line : other.kitchen
      if (otherResource !== resource) continue
      const otherWindow = singleWindow(other[field])
      if (otherWindow && overlaps(window, otherWindow)) {
        return `${other.trialId || 'another trial'}, hours ${sharedHours(window, otherWindow)}`
      }
    }
    return null
  }

  const windowCell = (
    params: GridRenderCellParams,
    field: 'lineWindow' | 'kitchenWindow'
  ) => {
    const row = params.row as TrialRowType
    // Each window is required exactly when its unit is used.
    const required = field === 'lineWindow' ? usesLine(row) : usesKitchen(row)
    const value = params.value as string | null | undefined
    const parsed = singleWindow(value)
    const clash = clashWith(row, field)
    const problem = usesNeither(row)
      ? field === 'lineWindow'
        ? 'Needs a line or a kitchen'
        : null
      : !parsed
        ? required
          ? isBlank(value)
            ? 'Required'
            : 'One window only, ending after it starts'
          : null
        : clash
          ? `Overlaps ${clash}`
          : null

    return (
      <Box
        onClick={() => setEditing({ row, field })}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          '&:hover .trial-edit-icon': { opacity: 1 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {problem ? (
            <CellWarning text={problem} />
          ) : (
            <DownTimeMiniTimeline value={value} />
          )}
        </Box>
        <EditIcon
          className="trial-edit-icon"
          sx={{ fontSize: 14, color: TOKENS.ink400, opacity: 0, flexShrink: 0 }}
        />
      </Box>
    )
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Trial ID',
        field: 'trialId',
        type: 'string',
        flex: 1,
        editable: true,
        description:
          'Identifies the trial, and is the key the row is saved under — so renaming a saved trial adds a second one rather than renaming it. It is the name the scheduler uses in any warning about this trial.',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: isBlank(params.props.value),
        }),
      },
      {
        headerName: 'Trial name',
        field: 'trialName',
        type: 'string',
        flex: 1,
        editable: true,
        description: 'What to call the trial on the schedule. Optional.',
      },
      {
        headerName: 'Line',
        field: 'line',
        type: 'singleSelect',
        flex: 1,
        editable: true,
        valueOptions: lineOptions,
        description:
          'The line the trial runs on. Leave blank for a kitchen-only trial — but a trial needs a line, a kitchen, or both.',
        // Clearing the line makes the trial kitchen-only, so its line window
        // goes with it rather than being left as an orphan the engine ignores.
        valueSetter: (value, row) => {
          const line = (value ?? '') as string
          if (isBlank(line)) return { ...row, line: '', lineWindow: '' }
          return { ...row, line }
        },
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error:
            isBlank(params.props.value) &&
            isBlank((params.row as TrialRowType)?.kitchen),
        }),
      },
      {
        headerName: 'Line window',
        field: 'lineWindow',
        type: 'string',
        flex: 1.4,
        // Opens the window editor rather than typing "(96,108)" into a cell.
        renderCell: (params: GridRenderCellParams) =>
          windowCell(params, 'lineWindow'),
      },
      {
        headerName: 'Needs kitchen',
        field: 'requiresKitchen',
        type: 'boolean',
        // Coerced: the stored value is the string 'True'/'False'.
        valueGetter: (value: unknown) => asBool(value),
        flex: 0.7,
        editable: true,
        description:
          'Tick when the trial also occupies a kitchen. The kitchen and its window are then both required. A trial with no line always uses its kitchen, ticked or not.',
      },
      {
        headerName: 'Kitchen',
        field: 'kitchen',
        type: 'singleSelect',
        flex: 1,
        editable: true,
        valueOptions: kitchenOptions,
        description:
          'Required when the trial needs a kitchen. Leave blank for a line-only trial — but a trial needs a line, a kitchen, or both.',
        // Picking a kitchen IS the planner saying the trial needs one, so tick
        // the box for them. The engine reads `requiresKitchen`, new rows default
        // it to false, and a row that named a kitchen without the tick used to
        // lose the kitchen half of the trial silently. Clearing the kitchen puts
        // the row back to line-only rather than leaving a half-filled claim.
        valueSetter: (value, row) => {
          const kitchen = (value ?? '') as string
          if (isBlank(kitchen)) {
            // Back to line-only — or, with no line either, to a row the
            // Line cell flags as needing one or the other.
            return {
              ...row,
              kitchen: '',
              kitchenWindow: '',
              requiresKitchen: false,
            }
          }
          return { ...row, kitchen, requiresKitchen: true }
        },
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error:
            isBlank(params.props.value) &&
            (asBool((params.row as TrialRowType)?.requiresKitchen) ||
              isBlank((params.row as TrialRowType)?.line)),
        }),
      },
      {
        headerName: 'Kitchen window',
        field: 'kitchenWindow',
        type: 'string',
        flex: 1.4,
        description:
          'Entered, not derived from the line window: a trial need not obey the usual kitchen-to-line delay, so deriving it would impose the very assumption the trial exists to test.',
        renderCell: (params: GridRenderCellParams) =>
          windowCell(params, 'kitchenWindow'),
      },
      {
        headerName: 'Product code',
        field: 'productCode',
        type: 'string',
        flex: 1,
        editable: true,
        description:
          'What is being trialled. Shown on the schedule only — a trial never counts towards this product’s demand, and a code the run does not know is accepted with a warning.',
      },
      {
        headerName: 'Active',
        field: 'active',
        type: 'boolean',
        // Coerced: the stored value is the string 'True'/'False'.
        valueGetter: (value: unknown) => asBool(value),
        flex: 0.6,
        editable: true,
        description: 'Untick to keep the row but leave it out of the run.',
      },
      // The four clean flags. Editable here as well as in the window popups
      // so they can be set in bulk and read at a glance, and so they survive
      // an upload round-trip like every other column.
      ...(
        [
          ['lineCleanBefore', 'Clean before (line)', 'line', 'before'],
          ['lineCleanAfter', 'Clean after (line)', 'line', 'after'],
          ['kitchenCleanBefore', 'Clean before (kitchen)', 'kitchen', 'before'],
          ['kitchenCleanAfter', 'Clean after (kitchen)', 'kitchen', 'after'],
        ] as const
      ).map(([field, headerName, unit, side]) => ({
        headerName,
        field,
        type: 'boolean' as const,
        // Absent means ON — see `asCleanFlag`. Without this an older trial
        // would show every clean unticked and read as though it had been
        // switched off deliberately.
        valueGetter: (value: unknown) => asCleanFlag(value),
        flex: 0.7,
        editable: true,
        description:
          `Whether the ${unit} is cleaned ${side} the trial. Untick and the ` +
          `trial books no cleaning on the ${unit} — the window just blocks ` +
          `those hours, exactly like a downtime window. On by default.`,
      })),
    ],
    [rows, lineOptions, kitchenOptions]
  )

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddRow}
          sx={{ textTransform: 'none' }}
        >
          Add trial
        </Button>
      </Box>
      <BaseTable
        key={gridKey}
        data={rows}
        columns={columns}
        isLoading={isLoading}
        updateTableData={saveWithChecks}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerTrials"
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: rows,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
      <DownTimeWindowsDialog
        open={editing != null}
        title={
          editing?.field === 'kitchenWindow' ? 'Kitchen window' : 'Line window'
        }
        resourceLabel={
          (editing?.field === 'kitchenWindow'
            ? editing?.row.kitchen
            : editing?.row.line) ||
          editing?.row.trialId ||
          ''
        }
        initialValue={editing ? editing.row[editing.field] : ''}
        maxWindows={MAX_TRIAL_WINDOWS}
        // Each popup asks about its own unit only: the line popup sets the
        // line's two flags, the kitchen popup the kitchen's. They are separate
        // rig reservations in the engine, so a planner can prepare the line
        // and leave the kitchen alone.
        cleanToggles={
          editing
            ? editing.field === 'kitchenWindow'
              ? {
                  unit: 'kitchen',
                  before: asCleanFlag(editing.row.kitchenCleanBefore),
                  after: asCleanFlag(editing.row.kitchenCleanAfter),
                }
              : {
                  unit: 'line',
                  before: asCleanFlag(editing.row.lineCleanBefore),
                  after: asCleanFlag(editing.row.lineCleanAfter),
                }
            : undefined
        }
        onClose={() => setEditing(null)}
        onSave={(value, cleans) => {
          if (editing) {
            const isKitchen = editing.field === 'kitchenWindow'
            const next: TrialRowType = {
              ...editing.row,
              [editing.field]: value,
              // Written on every save, not only when changed: the row may
              // never have carried these columns, and leaving them absent
              // would keep the popup showing a default the row does not
              // actually state.
              ...(cleans
                ? isKitchen
                  ? {
                      kitchenCleanBefore: cleans.before,
                      kitchenCleanAfter: cleans.after,
                    }
                  : {
                      lineCleanBefore: cleans.before,
                      lineCleanAfter: cleans.after,
                    }
                : {}),
            }
            // Same reasoning as the Kitchen column's valueSetter: entering a
            // kitchen window against a named kitchen is the tick, whichever of
            // the two the planner reaches for first.
            if (
              editing.field === 'kitchenWindow' &&
              !isBlank(value) &&
              !isBlank(next.kitchen)
            ) {
              next.requiresKitchen = true
            }
            saveWithChecks({ [editing.row.id]: next })
          }
          setEditing(null)
        }}
      />
      <Dialog
        open={pending != null}
        onClose={() => {
          setPending(null)
          setGridKey((k) => k + 1)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: FONT_SANS, fontSize: 16 }}>
          Check this trial
        </DialogTitle>
        <DialogContent sx={{ fontFamily: FONT_SANS, fontSize: 13 }}>
          <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
            {pending?.warnings.map((w) => (
              <Box component="li" key={w} sx={{ mb: 0.75 }}>
                {w}
              </Box>
            ))}
          </Box>
          {pending?.trialClash && (
            <Box sx={{ mt: 1.5 }}>
              If you proceed, the overlapping trials run at the same time. The
              line or kitchen they share is cleaned (where a trial asks for it)
              once before the first one starts and once after the last one ends,
              never in between.
            </Box>
          )}
          <Box sx={{ mt: 1.5, fontWeight: 700 }}>Do you want to proceed?</Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPending(null)
              setGridKey((k) => k + 1)
            }}
            sx={{ textTransform: 'none' }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pending) updateTableData(pending.changed)
              setPending(null)
            }}
            sx={{ textTransform: 'none' }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
